import { useEffect, useState, useContext, useMemo } from 'react';
import { UserContext } from './UserContext';
import { canonicalRole } from './utils/roleUtils.js';
import Sidebar from './Sidebar.js';
import { Card, Row, Col, Container, Form, Button, Modal, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Breadcrumb from './Breadcrumb';
import UserAddGeneral from './UserAddGeneral';
import UserAddAbout from './UserAddAbout';
import UserAddResidence from './UserAddResidence';
import api from './api/axiosInstance';
import ProfileMobileTabs from './ProfileMobileTabs';
import ProfileAccountModals from './ProfileAccountModals';
import ProfileUpdateSuccessModal from './components/Profile/ProfileUpdateSuccessModal';
import ProfileAccountSuccessModal, { useProfileAccountSuccess } from './components/Profile/ProfileAccountSuccessModal';
import icSucess from './assets/img/ic-celebration-circle.svg';
import icEye from './assets/img/ic-eye.svg';
import icEyeClosed from './assets/img/ic-eye-closed.svg';
import openPublicPdf from './utils/openPublicPdf';

const profileNavItems = [
    { to: '/profile', eventKey: 'about', label: 'About', end: true },
    { to: '/profile/academics', eventKey: 'academics', label: 'Academics' },
    { to: '/profile/work-preferences', eventKey: 'work', label: 'Work Preferences' },
    { to: '/profile/history', eventKey: 'history', label: 'History of changes' },
];

const UserProfileAbout = () => {
    const { user: actor, setUser } = useContext(UserContext);
    const id = String(actor?._id || '');
    const userRole = canonicalRole(actor?.role);
    const navigate = useNavigate();

    const [errors, setErrors] = useState({});
    const [profileImage, setProfileImage] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [showSavedModal, setShowSavedModal] = useState(false);
    const [savedModalMessage, setSavedModalMessage] = useState('');
    const [createdDate, setCreatedDate] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const { accountSuccessKind, showAccountSuccess, hideAccountSuccess } = useProfileAccountSuccess();
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [newPwd, setNewPwd] = useState('');
    const [confirmPwd, setConfirmPwd] = useState('');
    const [showNewPwd, setShowNewPwd] = useState(false);
    const [showConfirmPwd, setShowConfirmPwd] = useState(false);
    const [pwdMsg, setPwdMsg] = useState('');
    const [pwdSuccess, setPwdSuccess] = useState('');
    const [pwdLoading, setPwdLoading] = useState(false);
    const [pwdFading, setPwdFading] = useState(false);

    useEffect(() => {
        if (!pwdSuccess) return;
        setPwdFading(false);
        const fadeTimer = setTimeout(() => setPwdFading(true), 4000);
        const clearTimer = setTimeout(() => { setPwdSuccess(''); setPwdFading(false); }, 5000);
        return () => { clearTimeout(fadeTimer); clearTimeout(clearTimer); };
    }, [pwdSuccess]);

    const pwdChecks = useMemo(() => {
        const p = String(newPwd || '');
        return {
            minLen: p.length >= 8,
            lower: /[a-z]/.test(p),
            upper: /[A-Z]/.test(p),
            number: /\d/.test(p),
            special: /\W/.test(p),
        };
    }, [newPwd]);

    const pwdMatch = useMemo(() => {
        if (!newPwd && !confirmPwd) return null;
        return newPwd === confirmPwd;
    }, [newPwd, confirmPwd]);

    const [generalData, setGeneralData] = useState({
        profileImage: '',
        firstName: '',
        familyName: '',
        gender: '',
        company: '',
        companyRole: '',
        activityStatus: false,
    });

    const [aboutData, setAboutData] = useState({
        email: '',
        birthDate: '',
        phone: '',
        company: '',
        companyRole: '',
    });

    const [residenceData, setResidenceData] = useState({
        nationality: '',
        country: '',
        city: '',
        zipCity: '',
        citizenships: [],
    });

    const normalizeId = (x) => {
        if (!x) return '';
        if (typeof x === 'string') return x;
        if (typeof x === 'object') {
            if (x._id) return normalizeId(x._id);
            if (x.$oid) return String(x.$oid);
            if (x.id) return normalizeId(x.id);
            if (typeof x.toHexString === 'function') return x.toHexString();
            if (typeof x.toString === 'function' && x.toString !== Object.prototype.toString) {
                const s = x.toString();
                if (/^[a-fA-F0-9]{24}$/.test(s)) return s;
            }
        }
        return '';
    };

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const res = await api.get('/api/companies');
                setCompanies(res.data);
            } catch (err) {
                console.error('Error fetching companies:', err);
            }
        };
        fetchCompanies();
    }, []);

    useEffect(() => {
        if (!id) return;
        const fetchUserData = async () => {
            try {
                const res = await api.get(`/api/users/${id}`);
                const u = res.data;
                if (!u) return;

                setCreatedDate(u.createdAt || '');

                setGeneralData({
                    profileImage: u.profileImage || '',
                    firstName: u.firstName || '',
                    familyName: u.familyName || '',
                    gender: u.gender || '',
                    profileImagePreview: u.profileImage || '',
                    company: normalizeId(u.company),
                    companyRole: u.companyRole || '',
                    activityStatus: u.activityStatus || false,
                });

                setAboutData({
                    email: u.email || '',
                    birthDate: u.birthDate ? u.birthDate.slice(0, 10) : '',
                    phone: u.phone || '',
                    company: normalizeId(u.company),
                    companyRole: u.companyRole || '',
                });

                setResidenceData({
                    nationality: u.nationality || u.citizenships?.[0] || '',
                    country: u.country || '',
                    city: u.city || '',
                    zipCity: u.zipCity?.toString() || '',
                    citizenships: Array.isArray(u.citizenships)
                        ? u.citizenships.slice(0, 2)
                        : [u.citizenship1 || u.nationality || '', u.citizenship2 || ''].filter(Boolean),
                });
            } catch (err) {
                console.error('Error fetching user data:', err);
            }
        };
        fetchUserData();
    }, [id]);

    const validateAboutFields = () => {
        const newErrors = {};
        if (!aboutData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(aboutData.email)) {
            newErrors.email = 'Invalid email format';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateGeneralFields = () => {
        const newErrors = {};
        if (!generalData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!generalData.familyName.trim()) newErrors.familyName = 'Last name is required';
        if (!generalData.gender) newErrors.gender = 'Gender is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateResidenceFields = () => {
        const newErrors = {};
        if (!(residenceData.citizenships?.[0] || residenceData.nationality || '').trim())
            newErrors.citizenships = 'Citizenship #1 is required';
        if (!residenceData.country.trim()) newErrors.country = 'Country is required';
        if (!residenceData.city.trim()) newErrors.city = 'City is required';
        if (!String(residenceData.zipCity).trim()) newErrors.zipCity = 'Zip Code is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleGeneralChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'profileImage' && files?.length > 0) {
            const file = files[0];
            setProfileImage(file);
            setGeneralData((prev) => ({ ...prev, profileImagePreview: URL.createObjectURL(file) }));
            return;
        }
        setGeneralData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleGeneralSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!validateGeneralFields()) return false;
        try {
            const data = new FormData();
            data.append('firstName', generalData.firstName || '');
            data.append('familyName', generalData.familyName || '');
            data.append('gender', generalData.gender || '');
            if (profileImage) data.append('profileImage', profileImage);
            await api.put(`/api/users/${id}`, data);
            // Refresh the shared logged-in user (Header/Sidebar) from the canonical endpoint.
            try {
                const { data: me } = await api.get('/api/users/me');
                const currentUser = { ...me, role: canonicalRole(me.role) };
                localStorage.setItem('user', JSON.stringify(currentUser));
                if (typeof setUser === 'function') setUser(currentUser);
            } catch (refreshErr) {
                console.error('Error refreshing current user:', refreshErr);
            }
            setSavedModalMessage('General information updated successfully.');
            setShowSavedModal(true);
            return true;
        } catch (err) {
            console.error('Error updating general info:', err);
            return false;
        }
    };

    const handleAboutChange = (e) => {
        const { name, value } = e.target;
        setAboutData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleAboutSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!validateAboutFields()) return false;
        try {
            await api.put(`/api/users/${id}/about`, {
                email: aboutData.email,
                birthDate: aboutData.birthDate,
                phone: aboutData.phone,
                company: aboutData.company,
                companyRole: aboutData.companyRole,
            });
            setSavedModalMessage('About & contact information updated successfully.');
            setShowSavedModal(true);
            return true;
        } catch (err) {
            if (err.response?.status === 400 && err.response.data?.msg === 'User already exists') {
                setErrors((prev) => ({ ...prev, email: 'This email is already in use' }));
            } else {
                console.error('Error updating about info:', err);
            }
            return false;
        }
    };

    const handleResidenceChange = (e) => {
        const { name, value } = e.target;
        setResidenceData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleResidenceSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!validateResidenceFields()) return false;
        try {
            await api.put(`/api/users/${id}/residence`, {
                nationality: residenceData.citizenships?.[0] || residenceData.nationality || '',
                country: residenceData.country,
                city: residenceData.city,
                zipCity: residenceData.zipCity,
                citizenships: residenceData.citizenships || [],
                citizenship1: residenceData.citizenships?.[0] || '',
                citizenship2: residenceData.citizenships?.[1] || '',
            });
            setSavedModalMessage('Nationality & residence information updated successfully.');
            setShowSavedModal(true);
            return true;
        } catch (err) {
            console.error('Error updating residence info:', err);
            return false;
        }
    };

    const handleGeneralReset = (resetData) => { setGeneralData(resetData); setErrors({}); };
    const handleAboutReset = (resetData) => { setAboutData(resetData); setErrors({}); };
    const handleResidenceReset = (resetData) => { setResidenceData(resetData); setErrors({}); };

    const isActive = generalData.activityStatus;

    const handleDeactivate = async () => {
        try {
            const endpoint = isActive ? '/api/users/me/deactivate' : '/api/users/me/activate';
            await api.put(endpoint);
            setShowDeactivateModal(false);
            showAccountSuccess(isActive ? 'deactivated' : 'activated');
            setGeneralData((prev) => ({ ...prev, activityStatus: !isActive }));
        } catch (err) {
            console.error('Error toggling account status:', err);
        }
    };

    const handleDelete = async () => {
        try {
            await api.delete('/api/users/me');
            setShowDeleteModal(false);
            if (setUser) setUser(null);
            navigate('/');
        } catch (err) {
            console.error('Error deleting account:', err);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPwdMsg('');
        setPwdSuccess('');

        if (!newPwd || !confirmPwd) { setPwdMsg('Please enter and confirm your new password.'); return; }
        if (newPwd !== confirmPwd) { setPwdMsg('Passwords do not match.'); return; }
        const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{8,}$/;
        if (!passwordRegex.test(newPwd)) { setPwdMsg('Password must meet all requirements below.'); return; }

        try {
            setPwdLoading(true);
            await api.put('/api/users/me/change-password', { newPassword: newPwd });
            setNewPwd(''); setConfirmPwd('');
            setShowPasswordModal(false);
            showAccountSuccess('password');
        } catch (err) {
            setPwdMsg(err.response?.data?.msg || 'Failed to change password.');
        } finally {
            setPwdLoading(false);
        }
    };

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1 profile-page">
                <Container className="profile-page__container">
                    {pwdSuccess && (
                        <Row>
                            <Col>
                                <Alert variant="success" className="d-flex flex-row" style={{ transition: 'opacity 1s', opacity: pwdFading ? 0 : 1 }}>
                                    <strong>Password saved</strong>
                                </Alert>
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    
                    <Row>
                        <Col>
                            <Card className="profile-content-shell">
                                <Card.Body className="pt-0 profile-mobile">
                                    <Row>
                                        <Col xs={12}>
                                            <Row>
                                                <Col>
                                                    <Form onSubmit={handleGeneralSubmit}>
                                                        <UserAddGeneral
                                                            formData={{ ...generalData, company: aboutData.company, companyRole: aboutData.companyRole }}
                                                            profileImage={generalData.profileImagePreview}
                                                            handleChange={handleGeneralChange}
                                                            handleGeneralSubmit={handleGeneralSubmit}
                                                            errors={errors}
                                                            isEditPage={true}
                                                            handleReset={handleGeneralReset}
                                                            companies={companies}
                                                        />
                                                    </Form>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                    <ProfileMobileTabs items={profileNavItems} />
                                    <Row>
                                        <Col xs={12}>
                                            <Row className="profile-sections-row profile-mobile-content-card">
                                                <Col>
                                                    <Form onSubmit={handleAboutSubmit}>
                                                        <UserAddAbout
                                                            formData={aboutData}
                                                            handleChange={handleAboutChange}
                                                            handleAboutSubmit={handleAboutSubmit}
                                                            errors={errors}
                                                            isEditPage={true}
                                                            companies={companies}
                                                            createdDate={createdDate}
                                                            handleReset={handleAboutReset}
                                                            userRole={userRole}
                                                            hideCompanyFields={true}
                                                            disableEmail={userRole === 'student'}
                                                        />
                                                    </Form>
                                                </Col>
                                                <Col>
                                                    <Form onSubmit={handleResidenceSubmit}>
                                                        <UserAddResidence
                                                            formData={residenceData}
                                                            handleChange={handleResidenceChange}
                                                            handleResidenceSubmit={handleResidenceSubmit}
                                                            errors={errors}
                                                            isEditPage={true}
                                                            handleReset={handleResidenceReset}
                                                        />
                                                    </Form>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col className="d-flex justify-content-end gap-3 font-size-md profile-legal-links">
                                            <a className="text-primary fw-bold" href="/Abroadify-Privacy-Notice.pdf" target="_blank" rel="noopener noreferrer" onClick={(event) => openPublicPdf(event, '/Abroadify-Privacy-Notice.pdf')}>Privacy</a>
                                            <span className="profile-legal-links__separator d-lg-none" aria-hidden="true">·</span>
                                            <a className="text-primary fw-bold" href="/Abroadify-Terms-of-Use.pdf" target="_blank" rel="noopener noreferrer" onClick={(event) => openPublicPdf(event, '/Abroadify-Terms-of-Use.pdf')}>Terms of Use</a>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <div className="buttons-row profile-account-actions">
                        <Row>
                            <Col className="d-flex gap-3 justify-content-end">
                                <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)}>Delete account</Button>
                                <Button variant="outline-secondary" onClick={() => setShowDeactivateModal(true)}>{isActive ? 'Deactivate account' : 'Activate account'}</Button>
                                <Button variant="outline-secondary" onClick={() => { setShowPasswordModal(true); setPwdMsg(''); setPwdSuccess(''); setNewPwd(''); setConfirmPwd(''); }}>Change password</Button>
                            </Col>
                        </Row>
                    </div>
                </Container>
            </main>

            <ProfileUpdateSuccessModal show={showSavedModal} onHide={() => setShowSavedModal(false)} message={savedModalMessage} icon={icSucess} />
            <ProfileAccountSuccessModal kind={accountSuccessKind} onHide={hideAccountSuccess} />

            <ProfileAccountModals
                {...{ isActive, showDeactivateModal, setShowDeactivateModal, handleDeactivate,
                    showDeleteModal, setShowDeleteModal, handleDelete, showPasswordModal,
                    setShowPasswordModal, handleChangePassword, newPwd, setNewPwd, confirmPwd,
                    setConfirmPwd, showNewPwd, setShowNewPwd, showConfirmPwd, setShowConfirmPwd,
                    pwdChecks, pwdMatch, pwdMsg, setPwdMsg, pwdLoading, icEye, icEyeClosed }}
            />
        </>
    );
};

export default UserProfileAbout;
